import { useTranscriptionsState } from '@modules/transcriptions/useTranscriptionsState';
import { IconButton, Tooltip } from '@mui/material';
import cn from 'classnames';
import { useMemo } from 'react';
import { StyledIcon } from 'src/components/ActionButton/ActionButton';
import Iconify from 'src/components/iconify';
import { useTranscriptionNotesState } from 'src/features/ActionsBar/Gathering/GatheringTranscription/useTranscriptionNotesState';
import { useActionBarState } from 'src/features/ActionsBar/useActionBarState';
import { useTranscriptionControl } from 'src/hooks/useTranscriptionControl';
import { useLocales } from 'src/locales';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { RecordVideoTranscriptionPopover } from './RecordVideoTranscriptionPopover';
import { SettingsActionButton } from './SettingsActionButton';

type Props = {
  variant?: 'actionButton' | 'iconButton';
};

export const TranscriptionNotesButton = ({
  variant = 'actionButton',
}: Props) => {
  const { activeVoiceChannel } = useLivekitRTCState();
  const { translate } = useLocales();
  const { isTranscriptionStarted, startedByCurrentUser } =
    useTranscriptionNotesState();

  const { note, activeTranscriptionNote, setNote } = useTranscriptionsState();

  const { isOpen, setIsOpen, setReopen, setOpenAction } = useActionBarState();

  const { handleToggleTranscription } = useTranscriptionControl();

  const handleToggleNote = () => {
    if (!isOpen) {
      setNote(activeTranscriptionNote);
      return setOpenAction('notes');
    }
    if (note?._id === activeTranscriptionNote?._id) return setIsOpen(false);
    setNote(activeTranscriptionNote);
    setReopen('notes');
    setIsOpen(false);
  };

  const handleButtonClick = (audioOnly = true) => {
    (!isTranscriptionStarted || startedByCurrentUser) &&
    !activeTranscriptionNote?.loadingTranscription
      ? handleToggleTranscription(audioOnly)
      : handleToggleNote;
  };

  const tooltipText = useMemo(() => {
    if (isTranscriptionStarted && activeTranscriptionNote?.loadingTranscription)
      return translate(
        'communicate.rtc_controls.copera_is_loading_transcription',
      );
    if (!isTranscriptionStarted)
      return translate('communicate.rtc_controls.copera_start_transcription');
    if (startedByCurrentUser)
      return translate('communicate.rtc_controls.copera_stop_transcription');
    return translate('communicate.rtc_controls.copera_toogle_transcription');
  }, [
    isTranscriptionStarted,
    startedByCurrentUser,
    activeTranscriptionNote?.loadingTranscription,
    translate,
  ]);

  const noTranscribing =
    !isTranscriptionStarted ||
    !startedByCurrentUser ||
    activeTranscriptionNote?.loadingTranscription;

  if (variant === 'iconButton')
    return (
      <Tooltip title={tooltipText}>
        <IconButton
          size="small"
          onClick={() => handleButtonClick()}
          disabled={!!activeVoiceChannel?.channel?.egressId}
        >
          <Iconify
            icon={
              noTranscribing ? 'tabler:file-text-spark' : 'stop-transcription'
            }
            color={isTranscriptionStarted ? 'primary' : 'inherit'}
          />
        </IconButton>
      </Tooltip>
    );

  return (
    <SettingsActionButton
      popover={RecordVideoTranscriptionPopover}
      context={{
        onClick: () => handleButtonClick(false),
      }}
      onClick={() => handleButtonClick()}
      color={isTranscriptionStarted ? 'primary' : 'inherit'}
      tooltip={tooltipText}
      className={cn({ 'only-web': !startedByCurrentUser })}
      disabled={!!activeVoiceChannel?.channel?.egressId}
      hideActions={isTranscriptionStarted}
    >
      <StyledIcon
        icon={noTranscribing ? 'tabler:file-text-spark' : 'stop-transcription'}
      />
    </SettingsActionButton>
  );
};
